import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import Alert from '../../components/ui/Alert.jsx';
import { Facts, Loading, PageHeader, Pill, Section } from '../../components/ui/index.jsx';
import { fmtDateTime } from '../../utils/format.js';
import { AUTH_SOURCE, FORWARD_TYPE } from './inboxShared.jsx';
import { VerifiedSeal } from '../../components/ui/VerifiedSeal.jsx';

/** The servers an email passed through, oldest first. */
function Route({ hops, originIp, asn, emptyText }) {
  // Newest hop first in the headers; show the route in the order the email travelled
  const list = [...(hops || [])].reverse();
  const originIndex = list.findIndex((h) => h.ip && originIp === h.ip);
  if (!list.length) return <p className="muted">{emptyText}</p>;
  return (
    <ol className="route">
      {list.map((h, i) => (
        <li key={i} className={i === originIndex ? 'is-origin' : ''}>
          <span className="route-step" aria-hidden="true">{i + 1}</span>
          <div>
            <div className="mono break">{h.hidden ? <span className="muted">The forwarder’s own device or network (hidden for their privacy)</span> : <>{h.ptr || h.from || 'unknown host'}{h.ip && <span className="muted"> [{h.ip}]</span>}</>}</div>
            <div className="muted small">{h.by ? `to ${h.by}` : ''}{h.date ? ` · ${h.date}` : ''}{!h.public && h.ip ? ' · internal' : ''}</div>
            {i === originIndex && asn && <div className="small">Network: {asn}</div>}
          </div>
        </li>
      ))}
    </ol>
  );
}

const result = (v) => (v ? <Pill tone={v === 'pass' ? 'good' : v === 'fail' ? 'bad' : 'neutral'}>{v}</Pill> : <span className="muted">—</span>);

/** The original's full headers, the route it took and how it authenticated. */
export default function InboxHeaders() {
  const { messageId } = useParams();
  const { organisation } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!organisation) return;
    api(`/portal/organisations/${organisation.id}/inbox/${messageId}/headers`).then(setData).catch((err) => setError(err.message));
  }, [organisation, messageId]);

  const back = { to: `/inbox/${messageId}`, label: 'The email' };
  if (error) return <div className="page"><PageHeader back={back} title="Headers and route" /><Alert tone="danger">{error}</Alert></div>;
  if (!data) return <div className="page"><Loading /></div>;
  const o = data.original;
  const fwd = data.forward;
  const detail = o?.auth_detail || {};
  // Signatures we checked ourselves are a list; for an email sent straight to us, the
  // details are our mail server's own results ({ spf, dkim, dmarc } as words)
  const signatures = Array.isArray(detail.dkim) ? detail.dkim.filter((s) => s?.domain) : [];
  const serverChecks = !Array.isArray(detail.dkim) && ['spf', 'dkim', 'dmarc'].some((k) => typeof detail[k] === 'string') ? detail : null;
  const inline = !o || o.forward_type === 'inline';
  const direct = o?.forward_type === 'direct';

  const forwardChips = (
    <p className="small">From someone at @{data.message.forwarder_domain || '?'}:{' '}
      {['spf', 'dkim', 'dmarc'].map((k) => <span key={k} className="auth-chip">{k.toUpperCase()} {result(data.message.forwarder_auth[k])}</span>)}
    </p>
  );

  return (
    <div className="page">
      <PageHeader back={back} title="Headers and route" subtitle={data.message.subject || '(no subject)'} />
      <Alert tone="info">This page is for your IT team or SummitShield support. You don&rsquo;t need to understand it to decide whether an email is a scam.</Alert>

      {inline && (
        <>
          <Alert tone="warn">
            <strong>This email was forwarded as text</strong>, so its original headers weren&rsquo;t included: we can&rsquo;t see which servers it really came
            from, and anyone can type any &ldquo;From&rdquo; line into a forward. Below is what the forward says, and how the forward itself reached us.
            Ask people to use <strong>&ldquo;Forward as attachment&rdquo;</strong> (Outlook and Gmail both have it) so we can trace the original.
          </Alert>
          {o && (
            <Section title="As written in the forward" description="Copied from the text of the forward. Useful, but not proof of anything.">
              <Facts items={[
                ['From', o.from_address ? <span className="break">{o.from_name ? `${o.from_name} ` : ''}<span className="mono">&lt;{o.from_address}&gt;</span></span> : '—'],
                ['To', o.to || '—'],
                ['Sent', o.sent_at ? fmtDateTime(o.sent_at) : (o.sent_at_text || '—')],
                ['Subject', data.message.subject || '—'],
              ]} />
            </Section>
          )}
        </>
      )}

      {!inline && (
        <>
          <Section title={direct ? 'Authentication' : 'Authentication of the original'}>
            <Facts items={[
              ['How it reached us', FORWARD_TYPE[o.forward_type]],
              ['Who really sent it', o.origin_proven ? <span className="title-with-pill"><VerifiedSeal size={16} /> Confirmed</span> : <Pill tone="warn">Not confirmed</Pill>],
              ['How we know', AUTH_SOURCE[o.auth_source]],
              ['DMARC', result(o.dmarc)],
              serverChecks && ['Our mail server’s checks', <span>{['spf', 'dkim', 'dmarc'].map((k) => <span key={k} className="auth-chip">{k.toUpperCase()} {result(serverChecks[k])}</span>)}</span>],
              detail.recorded && ['Recorded by', `${detail.recorded.provider} (${detail.recorded.authserv})`],
              detail.recorded && ['Their result', <span className="mono break">{detail.recorded.raw}</span>],
              signatures.length > 0 && ['DKIM signatures', signatures.map((s, i) => <div key={i}>{result(s.result)} <span className="mono">{s.domain || '?'}{s.selector ? ` (${s.selector})` : ''}</span></div>)],
              ['Digitally signed by', o.dkim_domains?.length ? <span className="mono">{o.dkim_domains.join(', ')}</span> : <span className="muted">Not signed</span>],
              ['Undelivered mail returns to', o.return_path ? <span className="mono break">{o.return_path}</span> : '—'],
              ['Sending service', o.service || <span className="muted">Not recognised</span>],
              ['Message-ID', o.message_id ? <span className="mono break">{o.message_id}</span> : '—'],
            ]} />
            {!direct && (
              <>
                <h3 className="subhead">The forward itself</h3>
                {forwardChips}
                <p className="muted small">We only trust what an attached email says about itself when the forward passed DMARC — the check that proves the forward really came from the person who sent it.</p>
              </>
            )}
          </Section>

          <Section title={direct ? 'Route' : 'Route of the original'} description="The servers it passed through, oldest first. The highlighted step is where it left the sender.">
            <Route hops={o.hops} originIp={o.origin?.ip} asn={o.origin?.asn}
              emptyText="The original carries no route: it was sent to the person who forwarded it within the same email system, or the route was removed." />
          </Section>

          <Section title={direct ? 'All headers' : 'All headers of the original'}>
            <pre className="headers-raw">{o.headers_text || '(none)'}</pre>
          </Section>
        </>
      )}

      {!direct && fwd && (
        <Section title="How the forward reached us"
          description="The forward itself: from the person who sent it to us, to our check address. This isn’t evidence about the original — it shows the forward wasn’t tampered with on the way.">
          {inline && forwardChips}
          <Route hops={fwd.hops} emptyText="No route recorded." />
          <details className="details-block">
            <summary>All headers of the forward</summary>
            <pre className="headers-raw">{fwd.headers_text || '(none)'}</pre>
          </details>
          <p className="muted small">The forwarder&rsquo;s address and the network they sent from are hidden: they&rsquo;re a member of the public helping you.</p>
        </Section>
      )}
    </div>
  );
}
